import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';

import { AppPreferences } from '../core/services/app-preferences';
import { LandingHeader } from '../landing-page/components/landing-header/landing-header';
import { SupportCard } from '../landing-page/components/support-card/support-card';
import { LeadTrackingState } from '../lead-tracking/lead-tracking-state';
import { FundCalculatorApi } from '../personal-fund-request/services/fund-calculator-api';
import { AppIcon } from '../shared/app-icon/app-icon';

interface DetailMetric {
  label: string;
  value: string;
  isMoney?: boolean;
}

export interface TimelineStepView {
  statusId: number | null;
  name: string;
  date: string | null;
  formattedDate: string;
  isCurrent: boolean;
  isCompleted: boolean;
  isRejected: boolean;
}

interface RequestDetailsView {
  leadId: string;
  requestCode: string;
  requestId: string;
  title: string;
  offerTitle: string;
  status: string;
  statusClass: string;
  date: string;
  createdSinceText: string;
  amount: string;
  period: string;
  installment: string;
  profitRate: string;
  transferredAmount: string;
  profitAmount: string;
  administrativeFees: string;
  contractTotal: string;
  fundEntityName: string;
  fundEntityLogoUrl: string;
  isBank: boolean;
  showFundEntityName: boolean;
  timelineSteps: TimelineStepView[];
}

@Component({
  selector: 'app-request-details',
  imports: [LandingHeader, AppIcon, SupportCard],
  templateUrl: './request-details.html',
  styleUrl: './request-details.scss',
})
export class RequestDetailsComponent implements OnInit, OnDestroy {
  readonly preferences = inject(AppPreferences);
  private readonly router = inject(Router);
  private readonly trackingState = inject(LeadTrackingState);
  private readonly fundCalculatorApi = inject(FundCalculatorApi);

  details: RequestDetailsView | null = null;
  metrics: DetailMetric[] = [];
  isLoading = true;
  errorMessage = '';
  leadSupportEmail = '';
  leadSupportPhone = '';
  displayLogoUrl = this.getFallbackLogoUrl(true);
  isTrackingModalOpen = false;
  private detailsResponse: unknown = null;
  private logoSubscription: Subscription | null = null;
  private logoObjectUrl = '';
  private isShowingFallbackLogo = true;

  constructor() {
    effect(() => {
      this.preferences.theme();
      this.preferences.language();

      if (this.isShowingFallbackLogo) {
        this.setFallbackLogo(this.details?.isBank ?? true);
      }

      if (this.detailsResponse) {
        this.syncLocalizedDetails();
      }
    });
  }

  ngOnDestroy(): void {
    this.logoSubscription?.unsubscribe();
    this.revokeLogoObjectUrl();
  }

  async ngOnInit(): Promise<void> {
    const requestId = this.trackingState.getRequestId();

    if (!this.fundCalculatorApi.hasLeadAuthSession() || !requestId) {
      await this.router.navigateByUrl('/');
      return;
    }

    this.loadLeadSupportContactInfo();

    try {
      const response = await firstValueFrom(this.fundCalculatorApi.getLeadRequestDetails(requestId));

      this.detailsResponse = response;
      this.details = this.toDetailsView(response);
      this.metrics = this.getMetrics(this.details);
      this.loadLogo(this.details);
    } catch (error) {
      this.errorMessage = this.getErrorMessage(error);
    } finally {
      this.isLoading = false;
    }
  }

  private syncLocalizedDetails(): void {
    const currentLogoUrl = this.details?.fundEntityLogoUrl;
    const nextDetails = this.toDetailsView(this.detailsResponse);

    this.details = nextDetails;
    this.metrics = this.getMetrics(nextDetails);

    if (nextDetails.fundEntityLogoUrl !== currentLogoUrl) {
      this.loadLogo(nextDetails);
    }
  }

  async goBack(): Promise<void> {
    await this.router.navigateByUrl('/my-requests');
  }

  openTrackingModal(): void {
    this.isTrackingModalOpen = true;
  }

  closeTrackingModal(): void {
    this.isTrackingModalOpen = false;
  }

  private loadLeadSupportContactInfo(): void {
    this.fundCalculatorApi.getLeadCustomerSupportContactInfo().subscribe({
      next: (contactInfo) => {
        this.leadSupportEmail = contactInfo.supportEmail;
        this.leadSupportPhone = contactInfo.supportPhone;
      },
      error: () => {
        this.leadSupportEmail = '';
        this.leadSupportPhone = '';
      },
    });
  }

  private toDetailsView(response: unknown): RequestDetailsView {
    const body = this.toRecord(response);
    const data = body ? body['data'] ?? body['Data'] ?? response : response;
    const dataRecord = this.toRecord(data);
    const request = this.toRecord(
      dataRecord?.['request'] ?? dataRecord?.['Request'] ?? data,
    ) ?? {};
    const selectedOffer = this.toRecord(this.getCaseInsensitiveValue(request, 'selectedOffer')) ?? {};
    const amount = this.getNumberField(request, ['fundAmount', 'amount', 'requestAmount']);
    const period =
      this.getNumberField(request, ['fundPeriodInMonths', 'fundPeriod']) ??
      this.getNumberField(selectedOffer, ['fundPeriod']);
    const installment = this.getNumberField(selectedOffer, ['installmentAmount']);
    const profitRate =
      this.getNumberField(selectedOffer, ['annualProfitMarginPercent']) ??
      this.getNumberField(selectedOffer, ['apr']);
    const transferredAmount =
      this.getNumberField(selectedOffer, ['fundAmount']) ?? amount;

    const requestCode =
      this.getStringField(request, [
        'requestCode',
        'requestNumber',
        'requestId',
        'code',
        'number',
        'id',
        'leadId',
      ]) ||
      (dataRecord
        ? this.getStringField(dataRecord, [
            'requestCode',
            'requestNumber',
            'requestId',
            'code',
            'number',
            'id',
            'leadId',
          ])
        : '') ||
      this.trackingState.getRequestId();
    const leadId =
      this.getStringField(request, ['leadId']) ||
      (dataRecord ? this.getStringField(dataRecord, ['leadId']) : '') ||
      requestCode;

    return {
      leadId,
      requestCode,
      requestId: requestCode,
      title:
        this.getLocalizedField(request, 'fundType') ||
        this.preferences.t('myRequests.defaultRequestTitle'),
      offerTitle:
        this.getLocalizedOfferTitle(selectedOffer) ||
        this.getLocalizedField(request, 'fundType') ||
        this.preferences.t('myRequests.defaultRequestTitle'),
      status:
        this.getLocalizedField(request, 'status') ||
        this.getStringField(request, ['status', 'applicationStatus', 'requestStatus']) ||
        this.preferences.t('myRequests.defaultStatus'),
      statusClass: this.getStatusClass(request),
      date: this.formatDate(
        this.getStringField(request, ['requestDate', 'createdAt', 'createdOn', 'date']),
      ),
      createdSinceText: this.getCreatedSinceText(
        this.getStringField(request, ['requestDate', 'createdAt', 'createdOn', 'date']),
      ),
      amount: this.formatNumber(amount),
      period: period ? String(period) : '0',
      installment: this.formatNumber(installment),
      profitRate: profitRate === null ? '0%' : `${profitRate}%`,
      transferredAmount: this.formatNumber(transferredAmount),
      profitAmount: this.formatNumber(this.getNumberField(selectedOffer, ['profitAmount'])),
      administrativeFees: this.formatNumber(
        this.getNumberField(selectedOffer, ['administrativeFees', 'totalFeesValue']),
      ),
      contractTotal: this.formatNumber(this.getNumberField(selectedOffer, ['totalFundAmount'])),
      fundEntityName:
        this.getLocalizedStringFields(selectedOffer, 'fundEntityNameAr', 'fundEntityNameEn') ||
        'SNB',
      fundEntityLogoUrl: this.getLogoUrl(selectedOffer),
      isBank: this.getBooleanField(selectedOffer, ['isBank'], true),
      showFundEntityName: this.getBooleanField(request, ['showFundEntityName'], true),
      timelineSteps: this.getTimelineSteps(request, dataRecord),
    };
  }

  get logoImageUrl(): string {
    if (!this.details?.showFundEntityName) {
      return this.getFallbackLogoUrl(this.details?.isBank ?? true);
    }

    return this.displayLogoUrl;
  }

  bankDisplayName(): string | null {
    if (!this.details?.showFundEntityName) {
      return null;
    }

    return this.details.fundEntityName || null;
  }

  onLogoError(event: Event): void {
    const image = event.target as HTMLImageElement;
    const fallbackLogoUrl = this.getFallbackLogoUrl(this.details?.isBank ?? true);

    if (image.src.endsWith(fallbackLogoUrl)) {
      return;
    }

    this.setFallbackLogo(this.details?.isBank ?? true);
    image.src = this.displayLogoUrl;
  }

  private loadLogo(details: RequestDetailsView): void {
    this.logoSubscription?.unsubscribe();
    this.revokeLogoObjectUrl();
    this.setFallbackLogo(details.isBank);

    if (!details.showFundEntityName || !details.fundEntityLogoUrl) {
      return;
    }

    this.logoSubscription = this.fundCalculatorApi
      .getAuthenticatedFile(details.fundEntityLogoUrl)
      .subscribe({
        next: (blob) => {
          this.logoObjectUrl = URL.createObjectURL(blob);
          this.displayLogoUrl = this.logoObjectUrl;
          this.isShowingFallbackLogo = false;
        },
        error: () => {
          this.setFallbackLogo(details.isBank);
        },
      });
  }

  private setFallbackLogo(isBank: boolean): void {
    this.displayLogoUrl = this.getFallbackLogoUrl(isBank);
    this.isShowingFallbackLogo = true;
  }

  private getFallbackLogoUrl(isBank: boolean): string {
    const mode = this.preferences.theme() === 'dark' ? 'dark' : 'light';
    const entityType = isBank === false ? 'fund-entity' : 'bank';

    return `/assets/images/steps/${entityType}-error-${mode}.svg`;
  }

  private revokeLogoObjectUrl(): void {
    if (!this.logoObjectUrl) {
      return;
    }

    URL.revokeObjectURL(this.logoObjectUrl);
    this.logoObjectUrl = '';
  }

  private getLogoUrl(offer: Record<string, unknown>): string {
    const logoUrl = this.getStringField(offer, ['fundEntityLogoUrl', 'logoUrl']);

    if (logoUrl) {
      return logoUrl;
    }

    const logoFileId = this.getStringField(offer, ['fundEntityLogoFileId', 'logoFileId']);

    return logoFileId ? `Files/${logoFileId}` : '';
  }

  private getMetrics(details: RequestDetailsView): DetailMetric[] {
    return [
      {
        label: this.preferences.t('financeLabels.fundDuration'),
        value: `${details.period} ${this.preferences.t('financeLabels.month')}`,
      },
      {
        label: this.preferences.t('financeLabels.monthlyInstallment'),
        value: details.installment,
        isMoney: true,
      },
      {
        label: this.preferences.t('financeLabels.profitRate'),
        value: details.profitRate,
      },
      {
        label: this.preferences.t('financeLabels.transferredAmount'),
        value: details.transferredAmount,
        isMoney: true,
      },
      {
        label: this.preferences.t('financeLabels.profitAmount'),
        value: details.profitAmount,
        isMoney: true,
      },
      {
        label: this.preferences.t('financeLabels.administrativeFees'),
        value: details.administrativeFees,
        isMoney: true,
      },
    ];
  }

  private getLocalizedOfferTitle(offer: Record<string, unknown>): string {
    return this.getLocalizedStringFields(offer, 'offerTitleAr', 'offerTitleEn') ||
      this.getStringField(offer, ['offerTitle']);
  }

  private getLocalizedStringFields(
    body: Record<string, unknown>,
    arField: string,
    enField: string,
  ): string {
    const language = this.preferences.language();
    const preferredField = language === 'ar' ? arField : enField;
    const fallbackField = language === 'ar' ? enField : arField;

    return this.getStringField(body, [preferredField, fallbackField]);
  }

  private formatDate(value: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(this.normalizeUtcDateValue(value));

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const period = hours >= 12 ? 'pm' : 'am';
    const hour12 = String(hours % 12 || 12).padStart(2, '0');

    return `${year}-${month}-${day} ${hour12}:${minutes} ${period}`;
  }

  private normalizeUtcDateValue(value: string): string {
    const trimmedValue = value.trim();
    const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(trimmedValue);

    return hasTimezone ? trimmedValue : `${trimmedValue}Z`;
  }

  private formatNumber(value: number | null): string {
    return value === null ? '0' : value.toLocaleString('en-US');
  }

  private getNumberField(body: Record<string, unknown>, fields: string[]): number | null {
    for (const field of fields) {
      const value = this.getCaseInsensitiveValue(body, field);

      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }

      if (typeof value === 'string' && value.trim()) {
        const numericValue = Number(value.replace(/,/g, ''));

        if (Number.isFinite(numericValue)) {
          return numericValue;
        }
      }
    }

    return null;
  }

  private getBooleanField(
    body: Record<string, unknown>,
    fields: string[],
    defaultValue: boolean = false,
  ): boolean {
    for (const field of fields) {
      const value = this.getCaseInsensitiveValue(body, field);

      if (typeof value === 'boolean') {
        return value;
      }

      if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase();

        if (normalizedValue === 'true') {
          return true;
        }

        if (normalizedValue === 'false') {
          return false;
        }
      }
    }

    return defaultValue;
  }

  private getStringField(body: Record<string, unknown>, fields: string[]): string {
    for (const field of fields) {
      const value = this.getCaseInsensitiveValue(body, field);

      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    return '';
  }

  private getLocalizedField(body: Record<string, unknown>, field: string): string {
    const value = this.getCaseInsensitiveValue(body, field);
    const localizedValue = this.toRecord(value);

    if (!localizedValue) {
      return typeof value === 'string' ? value : '';
    }

    const language = this.preferences.language();
    const preferredValue = localizedValue[language];
    const fallbackValue = localizedValue[language === 'ar' ? 'en' : 'ar'];

    if (typeof preferredValue === 'string' && preferredValue.trim()) {
      return preferredValue.trim();
    }

    return typeof fallbackValue === 'string' ? fallbackValue.trim() : '';
  }

  private getCaseInsensitiveValue(body: Record<string, unknown>, field: string): unknown {
    return (
      body[field] ??
      body[field.charAt(0).toUpperCase() + field.slice(1)] ??
      body[field.toLowerCase()]
    );
  }

  private toRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return this.extractApiMessage(error.error) || this.preferences.t('common.states.error');
    }

    return error instanceof Error ? error.message : this.preferences.t('common.states.error');
  }

  private extractApiMessage(errorBody: unknown): string {
    const body = this.toRecord(errorBody);
    const message = body?.['message'];

    return typeof message === 'string' ? message : '';
  }

  private getStatusClass(request: Record<string, unknown>): string {
    const statusRecord = this.toRecord(this.getCaseInsensitiveValue(request, 'status'));
    const currentHistoryStatus = this.getCurrentHistoryStatus(request);
    const source = currentHistoryStatus ?? statusRecord;

    if (this.getBooleanField(source ?? request, ['isRejected'], false)) {
      return 'cancelled';
    }

    const englishName =
      this.getLocalizedEnglishName(source) ||
      this.getStringField(request, ['status', 'applicationStatus', 'requestStatus']);
    const normalizedName = englishName.trim().toLowerCase();

    if (
      normalizedName === 'under study' ||
      normalizedName === 'pending documents' ||
      normalizedName === 'submitted to financing entity'
    ) {
      return 'inprogress';
    }

    if (normalizedName === 'accepted') {
      return 'approved';
    }

    return '';
  }

  private getCurrentHistoryStatus(request: Record<string, unknown>): Record<string, unknown> | null {
    const historyValue =
      this.getCaseInsensitiveValue(request, 'statusHistory') ??
      this.getCaseInsensitiveValue(request, 'statuses') ??
      this.getCaseInsensitiveValue(request, 'requestStatuses');
    const history = Array.isArray(historyValue) ? historyValue : [];
    const current = history.find((item) => {
      const status = this.toRecord(item);

      return status ? this.getBooleanField(status, ['isCurrent'], false) : false;
    });

    return this.toRecord(current);
  }

  private getLocalizedEnglishName(value: Record<string, unknown> | null): string {
    if (!value) {
      return '';
    }

    const name = this.toRecord(this.getCaseInsensitiveValue(value, 'name'));
    const englishName = name?.['en'] ?? value['en'];

    return typeof englishName === 'string' ? englishName : '';
  }

  private getTimelineSteps(
    request: Record<string, unknown>,
    dataRecord?: Record<string, unknown> | null,
  ): TimelineStepView[] {
    const historyValue =
      this.getCaseInsensitiveValue(request, 'statusHistory') ??
      this.getCaseInsensitiveValue(request, 'statuses') ??
      this.getCaseInsensitiveValue(request, 'requestStatuses') ??
      (dataRecord
        ? this.getCaseInsensitiveValue(dataRecord, 'statusHistory') ??
          this.getCaseInsensitiveValue(dataRecord, 'statuses') ??
          this.getCaseInsensitiveValue(dataRecord, 'requestStatuses')
        : null);

    const history = Array.isArray(historyValue) ? historyValue : [];
    const language = this.preferences.language();

    return history
      .map((item) => this.toRecord(item))
      .filter((item): item is Record<string, unknown> => {
        if (!item) {
          return false;
        }

        const statusId = this.getNumberField(item, ['statusId', 'id']);

        // Ignore Draft status (statusId: 7)
        return statusId !== 7;
      })
      .map((item) => {
        const statusId = this.getNumberField(item, ['statusId', 'id']);
        const nameRecord = this.toRecord(this.getCaseInsensitiveValue(item, 'name'));
        const nameStr =
          (nameRecord ? (nameRecord[language] ?? nameRecord['ar'] ?? nameRecord['en']) : null) ??
          this.getStringField(item, ['name', 'statusName', 'title']);
        const isCurrent = this.getBooleanField(item, ['isCurrent'], false);
        const isCompleted = this.getBooleanField(item, ['isCompleted'], false);
        const isRejected = this.getBooleanField(item, ['isRejected'], false);
        const dateStr = this.getStringField(item, ['date', 'statusDate', 'createdAt', 'createdOn']);

        return {
          statusId,
          name: typeof nameStr === 'string' ? nameStr : '',
          date: dateStr || null,
          formattedDate: this.formatTimelineDate(dateStr),
          isCurrent,
          isCompleted,
          isRejected,
        };
      });
  }

  private formatTimelineDate(value: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(this.normalizeUtcDateValue(value));

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const language = this.preferences.language();

    try {
      return new Intl.DateTimeFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }).format(date);
    } catch {
      return this.formatDate(value);
    }
  }

  private getCreatedSinceText(rawDate: string): string {
    if (!rawDate) {
      return this.preferences.t('myRequests.details.createdOneDayAgo');
    }

    const createdDate = new Date(this.normalizeUtcDateValue(rawDate));

    if (Number.isNaN(createdDate.getTime())) {
      return this.preferences.t('myRequests.details.createdOneDayAgo');
    }

    const now = new Date();

    if (this.isSameLocalDate(createdDate, now)) {
      return this.preferences.t('myRequests.details.createdToday');
    }

    const diffMs = Math.max(0, now.getTime() - createdDate.getTime());
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const isAr = this.preferences.language() === 'ar';

    if (isAr) {
      if (diffDays === 1) {
        return this.preferences.t('myRequests.details.createdOneDayAgo');
      }

      if (diffDays === 2) {
        return this.preferences.t('myRequests.details.createdTwoDaysAgo');
      }

      const translationKey =
        diffDays > 10
          ? 'myRequests.details.createdManyDaysAgo'
          : 'myRequests.details.createdDaysAgo';

      return this.preferences.t(translationKey).replace('{{days}}', String(diffDays));
    }

    if (diffDays === 1) {
      return this.preferences.t('myRequests.details.createdOneDayAgo');
    }

    return this.preferences.t('myRequests.details.createdDaysAgo').replace('{{days}}', String(diffDays));
  }

  private isSameLocalDate(firstDate: Date, secondDate: Date): boolean {
    return firstDate.getFullYear() === secondDate.getFullYear() &&
      firstDate.getMonth() === secondDate.getMonth() &&
      firstDate.getDate() === secondDate.getDate();
  }
}
